/* eslint-disable no-shadow */
import React, {
	Dispatch,
	ReactElement,
	useCallback,
	useMemo,
	useRef,
	useState,
} from "react";
import { CheckBox, Column, Table } from "uikit";
import { SortType } from "rsuite-table";
import { UseTranslationResponse, useTranslation } from "react-i18next";
import { clone } from "lodash";

import {
	Executor,
	Language,
	Company,
	TaxiServiceOld as TaxiService,
} from "../../../../services";
import { getRoleDisplayName } from "../../../../assets/languages/langs";
import { useTypedSelector } from "../../../../redux/store";
import useModelSubscribe from "../../../../hooks/useModelSubscribe";
import mapByKey from "../../../../utils/mapByKey";
import SelectTabExtraColumn from "../../../../types/SelectTabExtraColumn";
import TableSettings from "../../../TableSettings";
import LightTable from "../../../LightTable";
import { useAppContext } from "../../../../context";

import { getColumns, defaultColumnIds } from "./constants";
import Header from "./components/Header";

interface ColumnContext {
	language: Language;
	companyById: Record<number, Company.Model>;
	taxiServiceById: Record<number, TaxiService.Model>;
	t: UseTranslationResponse<"translation", undefined>[0];
}

const Columns: Record<string, (context: ColumnContext) => ReactElement> = {
	fullName: ({ t }) => (
		<LightTable.Column width={400} sortable resizable>
			<LightTable.HeaderCell verticalAlign="middle">
				{t("mainPage.accounts.executors.columns.fullName") ?? ""}
			</LightTable.HeaderCell>
			<LightTable.Cell verticalAlign="middle" dataKey="fullName" fullText>
				{(item) => {
					const lastName = getRoleDisplayName(
						item.person?.lastName as any,
					);
					const firstName = getRoleDisplayName(
						item.person?.firstName as any,
					);
					const fatherName = getRoleDisplayName(
						item.person?.fatherName as any,
					);

					return [lastName, firstName, fatherName]
						.filter(Boolean)
						.join(" ");
				}}
			</LightTable.Cell>
		</LightTable.Column>
	),

	roles: ({ language, t }) => (
		<LightTable.Column width={140} sortable resizable>
			<LightTable.HeaderCell>
				{t("mainPage.accounts.executors.columns.role") ?? ""}
			</LightTable.HeaderCell>
			<LightTable.Cell dataKey="roles" fullText>
				{(rowData) =>
					rowData.roles
						?.map((role) =>
							getRoleDisplayName(role.name as any, language),
						)
						.filter(Boolean)
						.join(", ") ?? ""
				}
			</LightTable.Cell>
		</LightTable.Column>
	),

	alias: ({ t }) => (
		<LightTable.Column width={200} sortable resizable>
			<LightTable.HeaderCell verticalAlign="middle">
				{t("mainPage.accounts.executors.columns.alias") ?? ""}
			</LightTable.HeaderCell>
			<LightTable.Cell verticalAlign="middle" dataKey="alias" fullText>
				{(item) => item.alias}
			</LightTable.Cell>
		</LightTable.Column>
	),

	company: ({ language, companyById, taxiServiceById, t }) => (
		<LightTable.Column width={200} sortable resizable>
			<LightTable.HeaderCell verticalAlign="middle">
				{t("mainPage.accounts.executors.columns.company") ?? ""}
			</LightTable.HeaderCell>
			<LightTable.Cell verticalAlign="middle" dataKey="company" fullText>
				{(item) => {
					const { taxiService } = item;
					const companyId =
						taxiServiceById[taxiService.id]?.company?.id;

					const companyName = companyId
						? companyById[companyId]?.name[language] ?? ""
						: "";

					return (
						<div
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
								whiteSpace: "nowrap",
							}}
						>
							{companyName ?? ""}
						</div>
					);
				}}
			</LightTable.Cell>
		</LightTable.Column>
	),

	taxiService: ({ language, taxiServiceById, t }) => (
		<LightTable.Column width={200} sortable resizable>
			<LightTable.HeaderCell verticalAlign="middle">
				{t("mainPage.accounts.executors.columns.taxiService") ?? ""}
			</LightTable.HeaderCell>
			<LightTable.Cell
				verticalAlign="middle"
				dataKey="taxiService"
				fullText
			>
				{(item) => {
					const { taxiService } = item;

					const taxiServiceName =
						taxiServiceById[taxiService?.id]?.settlement[
							language
						] ?? "";

					return (
						<div
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
								whiteSpace: "nowrap",
							}}
						>
							{taxiServiceName ?? ""}
						</div>
					);
				}}
			</LightTable.Cell>
		</LightTable.Column>
	),
};

const ModelTable: React.FC<ModelTable.Props> = ({
	filters,
	selected,
	sort,
	disabled,
	disabledList = [],
	loading,
	data,
	language,
	allowedTaxiServiceIds,
	isShowMagnifyingGlass,
	extraColumn,
	hideSelection = false,
	onChangeFilters,
	onChangeSelected,
	onChangeSort,
	onLoadMore,
}) => {
	const [t] = useTranslation();
	const { dateFns } = useAppContext();
	const columns = useMemo(() => getColumns(t), [t]);
	const modelItems = useTypedSelector(
		(state) => state.globalState.executorRate.data.subscription.basic,
	);

	const canLoadMoreRef = useRef(true);
	const tableRef = useRef<Table.Ref | null>(null);

	const setItemSelection = useCallback(
		(id: number, value: boolean) => {
			if (value) {
				const newSelected = clone(selected);

				newSelected.push(id);

				onChangeSelected(newSelected);
			} else
				onChangeSelected(
					selected.filter((selectedId) => selectedId !== id),
				);
		},
		[onChangeSelected, selected],
	);

	const tableRowClassName = useCallback((item) => {
		if (!item?.active) return "not-active";

		return "";
	}, []);

	const tableOnScroll = useMemo(() => {
		canLoadMoreRef.current = true;

		return (x: number, y: number) => {
			if (!canLoadMoreRef.current) return;

			const contextHeight = data.length * 44;
			const top = Math.abs(y);
			const tableContainerHeight =
				tableRef.current?.root.getBoundingClientRect().height ?? 0;

			if (contextHeight - top - tableContainerHeight < 300) {
				onLoadMore();

				canLoadMoreRef.current = false;
			}
		};
	}, [data.length, onLoadMore]);

	const [columnIds, setColumnIds] = useState(defaultColumnIds);

	const companyData = useModelSubscribe({}, Company);

	const companies = useMemo(
		() => companyData?.cache ?? [],
		[companyData?.cache],
	);

	const companyById = useMemo(() => mapByKey(companies, "id"), [companies]);

	const { models: taxiServices } = useTypedSelector(
		(state) => state.taxiServices,
	);

	const taxiServiceById = useMemo(
		() => mapByKey(taxiServices, "id"),
		[taxiServices],
	);

	const columnContext = useMemo<ColumnContext>(
		() => ({ language, companyById, taxiServiceById, t }),
		[companyById, language, t, taxiServiceById],
	);

	// Колонки из настроек + доп. колонка сразу после `afterColumnId`.
	const tableColumns = useMemo(() => {
		const extraNode = extraColumn ? (
			<LightTable.Column
				key="extraColumn"
				width={extraColumn.width ?? 240}
				resizable
			>
				<LightTable.HeaderCell verticalAlign="middle">
					{extraColumn.label}
				</LightTable.HeaderCell>
				<LightTable.Cell verticalAlign="middle" fullText>
					{(item) => extraColumn.render(item as Executor.Model)}
				</LightTable.Cell>
			</LightTable.Column>
		) : null;

		let inserted = false;
		const nodes = columnIds.flatMap((columnId) => {
			const node = React.cloneElement(Columns[columnId](columnContext), {
				key: columnId,
			});
			if (extraNode && extraColumn?.afterColumnId === columnId) {
				inserted = true;
				return [node, extraNode];
			}
			return [node];
		});
		if (extraNode && !inserted) nodes.push(extraNode);

		return nodes;
	}, [columnContext, columnIds, extraColumn]);

	return (
		<Column sizes="auto! 1fr auto!" maxedWidth maxedHeight>
			<Header
				filters={filters}
				allowedTaxiServiceIds={allowedTaxiServiceIds}
				language={language}
				onChangeFilters={onChangeFilters}
				isShowMagnifyingGlass={isShowMagnifyingGlass}
			/>
			<LightTable
				ref={tableRef}
				fillHeight
				virtualized
				shouldUpdateScroll={false}
				headerHeight={44}
				rowHeight={44}
				sortColumn={sort.column}
				sortType={sort.type}
				loading={loading}
				data={data}
				rowClassName={tableRowClassName}
				onScroll={tableOnScroll}
				onSortColumn={(column, type) => onChangeSort({ column, type })}
			>
				{!hideSelection && (
					<LightTable.Column width={36}>
						<LightTable.HeaderCell verticalAlign="middle">
							<CheckBox
								value={
									!!data.length &&
									data.every((item) =>
										selected.includes(item.id),
									)
								}
								disabled={disabled}
								onChange={(value) =>
									onChangeSelected(
										value
											? selected.concat(
													data
														.filter(
															(item) =>
																!selected.includes(
																	item.id,
																),
														)
														.map((item) => item.id),
											  )
											: selected.filter((id) => {
													if (
														disabledList.includes(
															id,
														)
													) {
														return true;
													}

													return !data.find(
														(item) =>
															item.id === id,
													);
											  }),
									)
								}
							/>
						</LightTable.HeaderCell>
						<LightTable.Cell verticalAlign="middle">
							{(item) => (
								<CheckBox
									value={selected.includes(item.id)}
									disabled={
										disabled ||
										disabledList?.includes(item.id)
									}
									onChange={(selected) =>
										setItemSelection(item.id, selected)
									}
								/>
							)}
						</LightTable.Cell>
					</LightTable.Column>
				)}
				<LightTable.Column width={200} resizable>
					<LightTable.HeaderCell verticalAlign="middle">
						{t(
							"mainPage.accounts.executors.columns.executorRateId",
						) ?? ""}
					</LightTable.HeaderCell>
					<LightTable.Cell
						verticalAlign="middle"
						dataKey="rate"
						fullText
					>
						{(item) => {
							const rateName =
								modelItems?.find(
									(rate) => rate?.id === item?.rate?.id,
								)?.name ?? "";

							if (item?.nextRateId) {
								const nextName =
									modelItems?.find(
										(rate) => rate.id === item.nextRateId,
									)?.name ?? "";
								const time = item?.nextRateStart
									? dateFns.toZonedTime(
											new Date(item.nextRateStart),
											dateFns.timezone,
									  )
									: "";

								return `${rateName} > ${nextName} ${
									time
										? `(${dateFns.format(time, language)}) `
										: ""
								}`;
							}

							return rateName || "";
						}}
					</LightTable.Cell>
				</LightTable.Column>

				{tableColumns}
			</LightTable>
			<TableSettings
				value={columnIds}
				defaultValue={defaultColumnIds}
				columns={columns}
				onChange={setColumnIds}
			/>
		</Column>
	);
};

declare namespace ModelTable {
	interface Sort {
		column?: string;
		type?: SortType;
	}

	interface Props {
		filters: Header.Filters.Value;
		selected: number[];
		sort: Sort;

		disabled: boolean;
		disabledList?: number[];
		loading: boolean;
		data: Executor.Model[];
		language: Language;
		allowedTaxiServiceIds: number[];
		isShowMagnifyingGlass: boolean;
		/** Доп. колонка справа (не входит в настройки колонок). */
		extraColumn?: SelectTabExtraColumn<Executor.Model>;
		/** Без колонки с галками (режим просмотра). */
		hideSelection?: boolean;

		onChangeFilters: Dispatch<Header.Filters.Value>;
		onChangeSelected: Dispatch<number[]>;
		onChangeSort: Dispatch<Sort>;

		onLoadMore: () => void;
	}
}

export default ModelTable;
